import {
  FlatVisits,
  GroupedLabels,
  Label,
} from '@restore/clinicalTrialContext/clinicalTrialContext.types';
import { parseDateOnly, shortDateUS } from '@dynamika-shared/utils/date';
import { UploadFileTypeEnum } from '@dynamika-shared/types/inventory';
import {
  QCTReading,
  QCTScanTypeEnum,
  QCTScoreData,
  QCTVisitDataDTO,
  QCTVisitLabeledScore,
  QCTWorkstationDataDTO,
  QCTWorkstationEntry,
} from '@dynamika-shared/types/QCTReading';

// Shared empty result, so the selector below keeps a stable reference.
const NO_LABELS: Label[] = [];

export const selectQCTLabels = (
  groupedLabels: GroupedLabels | object
): Label[] => (groupedLabels as GroupedLabels)?.QCT ?? NO_LABELS;

export const selectQCTVisitLabels = (
  flatVisits: FlatVisits | undefined,
  visitDefinitionId: string | undefined
): Label[] | null => {
  const visit = visitDefinitionId ? flatVisits?.[visitDefinitionId] : undefined;

  if (!visit) {
    return null;
  }

  return Object.values(visit.labels ?? {}).filter(
    label => label.dataType === UploadFileTypeEnum.QCT
  );
};

const CALENDAR_DATE = /^\d{4}-\d{2}-\d{2}/;

export const toAcquisitionDate = (
  examDate: string | null | undefined
): string | null => {
  if (!examDate) {
    return null;
  }

  const trimmed = examDate.trim();

  const [datePart] = CALENDAR_DATE.exec(trimmed) ?? [];

  const parsed = parseDateOnly(datePart ?? trimmed);

  return parsed ? shortDateUS(parsed) : null;
};

export const QCTScanTypeLabels: Record<QCTScanTypeEnum, string> = {
  [QCTScanTypeEnum.LUMBAR_SPINE]: 'Lumbar Spine',
  [QCTScanTypeEnum.HIP]: 'Hip',
};

export const filterQCTLabels = (
  groupedLabels: Label[],
  visitData: QCTVisitDataDTO | null
): Label[] => {
  if (!visitData?.data?.length) {
    return groupedLabels;
  }

  const assignedLabelIds = new Set(
    visitData.data.map(score => score.labelId)
  );

  return groupedLabels.filter(label => !assignedLabelIds.has(label.id));
};

export const findLabelName = (
  labelId: string,
  labels: Label[]
): string | undefined => labels.find(label => label.id === labelId)?.name;

export const toVisitReadings = (
  visitData: QCTVisitDataDTO | null,
  groupedLabels: Label[]
): QCTReading[] | undefined => {
  if (!visitData) {
    return undefined;
  }

  return (visitData.data ?? []).map((score, index) => ({
    ...parseWorkstationEntry(score.data),
    entry: score.data,
    referenceId: score.referenceId,
    rowKey: `${score.referenceId}:${index}`,
    scoreIndex: index,
    labelId: score.labelId,
    labelName: score.labelId
      ? findLabelName(score.labelId, groupedLabels)
      : '',
  }));
};

const toScanType = (
  bodyProperty: string | null | undefined
): QCTScanTypeEnum | null => {
  const value = bodyProperty?.toLowerCase() ?? '';

  if (value.includes('hip')) {
    return QCTScanTypeEnum.HIP;
  }

  if (value.includes('spine')) {
    return QCTScanTypeEnum.LUMBAR_SPINE;
  }

  return null;
};

export const parseWorkstationEntry = (
  entry: QCTWorkstationEntry
): QCTScoreData => ({
  fileName: entry.name ?? null,
  scanType: toScanType(entry.bodyProperty),
  examDate: entry.examDate ?? null,
});

const isWorkstationEntry = (value: unknown): value is QCTWorkstationEntry =>
  typeof value === 'object' && value !== null;

const toWorkstationEntries = (
  data: QCTWorkstationDataDTO['data']
): QCTWorkstationEntry[] => {
  if (Array.isArray(data)) {
    return data.filter(isWorkstationEntry);
  }

  if (!isWorkstationEntry(data)) {
    return [];
  }

  return [data as QCTWorkstationEntry];
};

export const toWorkstationReadings = (
  workstationData: QCTWorkstationDataDTO[]
): QCTReading[] =>
  workstationData.flatMap(record =>
    toWorkstationEntries(record.data).map((entry, index) => ({
      ...parseWorkstationEntry(entry),
      entry,
      referenceId: record.id,
      rowKey: `${record.id}:${index}`,
      labelId: null,
    }))
  );
export const toLabeledScores = (
  readings: QCTReading[]
): QCTVisitLabeledScore[] =>
  readings.map(reading => ({
    referenceId: reading.referenceId,
    labelId: reading.labelId,
    data: reading.entry,
  }));

const scanKey = (
  referenceId: string,
  scanType: QCTScanTypeEnum | null | undefined,
  fileName: string | null | undefined
): string => [referenceId, scanType ?? '', fileName ?? ''].join('|');

const scoreScanKey = (score: QCTVisitLabeledScore): string =>
  scanKey(
    score.referenceId,
    toScanType(score.data?.bodyProperty),
    score.data?.name
  );

const readingScanKey = (reading: QCTReading): string =>
  scanKey(reading.referenceId, reading.scanType, reading.fileName);

export const excludeVisitReadings = (
  workstationReadings: QCTReading[],
  visitReadings: QCTReading[]
): QCTReading[] => {
  if (!visitReadings.length) {
    return workstationReadings;
  }

  const storedCounts = new Map<string, number>();

  visitReadings.forEach(reading => {
    const key = readingScanKey(reading);

    storedCounts.set(key, (storedCounts.get(key) ?? 0) + 1);
  });

  return workstationReadings.filter(reading => {
    const key = readingScanKey(reading);

    const stored = storedCounts.get(key) ?? 0;

    if (!stored) {
      return true;
    }

    storedCounts.set(key, stored - 1);

    return false;
  });
};

export const mergeLabeledScores = (
  stored: QCTVisitLabeledScore[],
  incoming: QCTVisitLabeledScore[]
): QCTVisitLabeledScore[] => {
  const positionsByScan = new Map<string, number[]>();

  stored.forEach((score, index) => {
    const key = scoreScanKey(score);

    positionsByScan.set(key, [...(positionsByScan.get(key) ?? []), index]);
  });

  const merged = [...stored];

  const appended: QCTVisitLabeledScore[] = [];

  incoming.forEach(score => {
    const position = positionsByScan.get(scoreScanKey(score))?.shift();

    if (position === undefined) {
      appended.push(score);
    } else {
      merged[position] = score;
    }
  });

  return [...merged, ...appended];
};

/**
 * Counts selections that still refer to a row currently on screen.
 *
 * A re-sync can drop rows the specialist had already picked; those stale
 * entries must not inflate the "N values selected for reading" confirmation.
 * Selections are keyed by `rowKey`, so a record reporting several scans is
 * counted per scan rather than per record.
 */
export const countActiveSelections = (
  selected: Record<string, string>,
  presentRowKeys: Set<string>
): number =>
  Object.entries(selected).filter(
    ([rowKey, labelId]) => !!labelId && presentRowKeys.has(rowKey)
  ).length;
